<script setup>

import {computed, onActivated, watch} from 'vue';
import ImageGallery from "~/components/molecules/ImageGallery.vue";
import GreyBlock from "~/components/templates/GreyBlock.vue";
import Reviews from "~/components/organisms/Reviews.vue";
import ProductMainDescription from "~/components/organisms/ProductMainDescription.vue";
import CollectionsPreview from "~/components/organisms/CollectionsPreview.vue";
import BasicBlock from "~/components/molecules/BasicBlock.vue";
import SalesArguments from "~/components/plugins/SalesArguments/SalesArguments.vue";
import ProductsCarousel from "~/components/organisms/ProductsCarousel.vue";
import Instagram from "~/components/organisms/Instagram.vue";
import ArticlesCarousel from "~/components/organisms/ArticlesCarousel.vue";
import ProductMainInfo from "~/components/organisms/ProductMainInfo.vue";
import ProductSpoiler from "~/components/organisms/ProductSpoiler.vue";
import Image from "~/components/atoms/Image.vue";
import BetterWith from "~/components/organisms/BetterWith.vue";
import Text from "~/components/atoms/Text.vue";
import SampleForm from "~/components/organisms/SampleForm.vue";
import Modal from "~/components/molecules/Modal.vue";
import Breadcrumbs from "~/components/organisms/Breadcrumbs.vue";
import {useLastVisitedProducts} from "~/store/lastVisitedProducts.js";
import {fetchWithErrorHandling} from '~/composables/fetchWithErrorHandling';
import {useCustomFetch} from '~/composables/customFetch.js';
import {consumeProductViewContext, getProductViewSessionId} from '~/utils/productViewContext.js';
import {useUserStore} from "~/store/userStore.js";
import AdminButton from "~/components/molecules/AdminButton.vue";
import Button from "~/components/atoms/Button.vue";
import Supplementary from "~/components/organisms/Supplementary.vue";
import ExpandIcon from "~/components/icons/ExpandIcon.vue";
import {useCurrencyStore} from "~/store/currencyStore.js";
import Shortcode from "~/components/molecules/Shortcode.vue";
import ShopTheLook from "~/components/organisms/ShopTheLook.vue";

import ProductSelectSide from '~/components/organisms/ProductSelectSide.vue'

const __productChunkBuildBump = '2026-06-02'
void __productChunkBuildBump

const {t} = useI18n();

const route = useRoute()
const slug = route.params.slug.join('/');
const lastVisitedProductsStore = useLastVisitedProducts()
const currencyStore = useCurrencyStore()
const userStore = useUserStore()

if (route.query.utm_source === 'showroom') {
  useCookie('showroom_client', {
    path: '/',
    maxAge: 60 * 60 * 24 * 30,
  }).value = new Date().toISOString();
}

let data = await fetchWithErrorHandling(`product/${slug}`)

const {product, reviews} = data?.value ?? {}
const blog_articles = computed(() => product?.blog_articles ?? [])

if (product) {
  useHead({
    title: `${product.name} ${product.name.length < 54 ? '| Hill Ceramic®' : ''}`,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: t('product.meta.description', {name: product.name, sku: product.sku}),
      },
      {
        hid: 'og:title',
        property: 'og:title',
        content: product.name + (product.name.length < 54 ? ' | Hill Ceramic®' : '')
      },
      {hid: 'og:image', property: 'og:image', content: t('meta.domain') + '/img/og-default.jpg'}
    ],
    script: [
      {
        type: 'application/ld+json',
        children: product.breadcrumbs.structured_data,
      },
      {
        type: 'application/ld+json',
        children: product.structured_data?.data,
      },
      {
        type: 'application/ld+json',
        children: product.images_structured_data?.data,
      },
    ]
  })

  lastVisitedProductsStore.addProduct(product.id)
}

const showSampleForm = ref(false)

const supplementary = computed(() => {
  if (!product) {
    return []
  }

  return (product.supplementary.mosaic ? [product.supplementary.mosaic] : []).concat(product.supplementary.supplementary_bundles.concat(product.supplementary.combo))
})

const mainTexts = computed(() => {
  if (!product?.new_texts?.main) return []

  const typesConfig = {
    important: {
      titleKey: 'product.important_info',
      class: 'main-info-description bg-blue'
    },
    good_to_know: {
      titleKey: 'product.good_to_know',
      class: 'main-info-description bg-green'
    },
    basic: {
      titleKey: 'base.information',
      class: ''
    }
  }

  return Object.entries(product.new_texts.main)
      .map(([type, value]) => {
        const items = Array.isArray(value) ? [...value] : [value]
        let content = ''
        let priority = 999

        items.sort((a, b) => {
          const pA = (a && typeof a === 'object' && a.priority !== undefined) ? a.priority : 999
          const pB = (b && typeof b === 'object' && b.priority !== undefined) ? b.priority : 999
          return pA - pB
        }).forEach(item => {
          if (typeof item === 'string') {
            content += item
          } else if (item && typeof item === 'object') {
            content += item.content || ''
            if (item.priority !== undefined) priority = Math.min(priority, item.priority)
          }
        })

        return {
          type,
          titleKey: typesConfig[type]?.titleKey,
          title: typesConfig[type]?.title || type,
          class: typesConfig[type]?.class || '',
          content,
          priority
        }
      })
      .filter(item => item.content)
      .sort((a, b) => a.priority - b.priority)
})

const bottomText = computed(() => {
  const basic = product?.new_texts?.bottom?.basic
  if (!basic) return null

  const items = Array.isArray(basic) ? [...basic] : [basic]
  let content = ''

  items.sort((a, b) => {
    let pA = (a && typeof a === 'object' && a.priority !== undefined) ? a.priority : 999
    let pB = (b && typeof b === 'object' && b.priority !== undefined) ? b.priority : 999
    if (pA === 0) pA = 999
    if (pB === 0) pB = 999
    return pA - pB
  }).forEach(item => {
    if (typeof item === 'string') {
      content += item
    } else if (item && typeof item === 'object') {
      content += item.content || ''
    }
  })

  return content || null
})


const hoveredImage = ref(null)

const {$device} = useNuxtApp()

const resolveFileImage = (image) => {
  if (!image) {
    return null
  }

  if ($device.isMobile && image.mobile_url) {
    return image.mobile_url
  }

  return image.url || null
}

const image1 = computed(() => {
  if (!product) {
    return null
  }

  const image = resolveFileImage(product.files.interior_images[0])

  if (image) {
    return image
  }

  return resolveFileImage(product.files.frontal_images[0])
})

const image2 = computed(() => {
  if (!product) {
    return null
  }

  let image = resolveFileImage(product.files.interior_images[1])

  if (image) {
    return image
  }

  image = resolveFileImage(product.files.frontal_images[1])

  if (!image) {
    return image1.value
  }

  return image
})

const activeSelect = ref(null)

const currentBundle = ref(null)
const bundleToken = ref(null)

const rightColumn = ref(null)
const rightColumnStickyTop = ref(10)
const rightColumnStyle = computed(() => ({
  top: `${rightColumnStickyTop.value}px`,
}))

const updateRightColumnStickyTop = () => {
  if (!import.meta.client || !rightColumn.value) {
    return
  }

  // Mobile: sticky is disabled via CSS
  if (window.innerWidth <= 991) {
    rightColumnStickyTop.value = 0
    return
  }

  const offset = 10
  const bottomGap = 16
  const height = rightColumn.value.offsetHeight

  // Tall sidebar: negative top lets it scroll through first, then stick with bottom in view.
  // Short sidebar: stick near the top as before.
  rightColumnStickyTop.value = Math.min(offset, window.innerHeight - height - bottomGap)
}

let rightColumnResizeObserver = null

// При keepalive реактивація — скидаємо ключ щоб carousels перемонтувались
const carouselKey = ref(0)
onActivated(() => {
  carouselKey.value++
  nextTick(updateRightColumnStickyTop)
})
watch(() => route.params.slug, () => {
  carouselKey.value++
  nextTick(updateRightColumnStickyTop)
})

onMounted(() => {
  nextTick(() => {
    updateRightColumnStickyTop()

    if (rightColumn.value && typeof ResizeObserver !== 'undefined') {
      rightColumnResizeObserver = new ResizeObserver(() => updateRightColumnStickyTop())
      rightColumnResizeObserver.observe(rightColumn.value)
    }

    window.addEventListener('resize', updateRightColumnStickyTop)
  })

  if (product) {
    const ctx = consumeProductViewContext(product.id)

    useCustomFetch('page-view', {
      method: 'POST',
      body: {
        viewable_type: 'product',
        product_id: product.id,
        category_id: ctx?.category_id ?? null,
        position: ctx?.position ?? null,
        page: ctx?.page ?? null,
        filters: ctx?.filters ?? null,
        block: ctx?.block ?? null,
        source_url: ctx?.source_url ?? null,
        session_id: getProductViewSessionId(),
        source: 'web',
        referrer: document.referrer || null,
      },
    }, true).catch(() => {
    })

    preloadOnIdle(() => preloadProductPageChunks({hasSelects: Boolean(product.selects)}))

    if (typeof (dataLayer) != "undefined" && dataLayer !== null) {
      dataLayer?.push({ecommerce: null});
      dataLayer?.push({
        'event': 'view_item',
        'currency': 'SEK',
        'value': Math.ceil((product.prices?.mainPrice ?? 1) / currencyStore.rate),
        'ecommerce': {
          'items': [{
            ...product.data_layer?.ga4.item[0]
          }]
        }
      });

      dataLayer?.push({ecommerce: null});
      dataLayer?.push({
        'event': 'productDetails',
        'ecommerce': {
          'detail': {
            'products': [{
              ...product.data_layer?.ga3.products[0]
            }]
          }
        }
      });
    }

    if (process.client) {
      var _learnq = window._learnq;

      _learnq?.push(["track", "Viewed Product", product.data_layer?.klaviyo]);
    }
  }
})

onBeforeUnmount(() => {
  rightColumnResizeObserver?.disconnect()
  if (import.meta.client) {
    window.removeEventListener('resize', updateRightColumnStickyTop)
  }
})
</script>

<template>
  <template v-if="product">
    <div class="container product-page g-16">
      <div class="main-info full-width flex-start">
        <div class="left-column gallery">
          <div class="product-breadcrumbs" style="margin-bottom: 16px;">
            <Breadcrumbs :breadcrumbs="product.breadcrumbs.data"/>
          </div>

          <ImageGallery :key="carouselKey" :hoveredImage="hoveredImage"
                        :timer_date="Number(product.supplier_id) === 196 ? null : (product.timer ? product.timer.date : null)"
                        :full_images="product.files.full_images"
                        :previews="product.files.previews"/>
        </div>
        <div
            ref="rightColumn"
            class="right-column mobile-padding"
            :style="rightColumnStyle"
        >
          <Text class="sku">SKU: {{ product.sku }}</Text>
          <ProductMainInfo
              :bundleToken="bundleToken"
              @bundleChanged="currentBundle=$event"
              @selectChange="activeSelect=$event"
              @alternativeHover="hoveredImage = $event"
              @alternativeUnhover="hoveredImage = null"
              :optionalSupplementary="supplementary?.filter(b=>b.optional)"
              :supplementary="product.supplementary.supplementary_bundles.filter(bundle => product.supplementary.supplementary_bundles_mandatory.map(item=>item.id).includes(bundle.id))"
              @show-sample-form="showSampleForm=true" :product="product"/>
        </div>

        <div class="left-column">
          <div class="g-24">
            <template v-if="mainTexts.length">
              <div v-for="text in mainTexts" :key="text.type"
                   class="description-info mobile-padding" :class="text.class">
                <Text tag="h2">{{ text.titleKey ? $t(text.titleKey) : text.title }}:</Text>
                <div v-html="text.content || ''"></div>
              </div>
            </template>
            <template v-else>
              <div class="description-info main-info-description mobile-padding" v-if="product.main_info_description">
                <Text tag="h2">{{ $t('product.important_info') }}:</Text>
                <div v-html="product.main_info_description || ''"></div>
              </div>
              <div class="description-info mobile-padding" v-if="product.info_description">
                <Text tag="h2">{{ $t('base.information') }}:</Text>
                <div v-html="product.info_description || ''"></div>
              </div>
            </template>

            <section v-if="product.bundles">
              <BetterWith :label="$t('product.package_includes')" :title="$t('base.contents')" :sku="product.sku"
                          :items="product.bundles.products"/>
            </section>

            <section v-if="product.better_deal?.length">
              <BetterWith :label="$t('product.better_deal_label')" :title="$t('product.better_deal')" :sku="product.sku"
                          :items="product.better_deal"/>
            </section>

            <div>
              <section v-if="supplementary?.filter(b=>!b.optional).length">
                <BetterWith
                    :label="$t('product.best_combination')"
                    :title="$t('product.better_with')" :sku="product.sku"
                    :items="supplementary?.filter(b=>!b.optional)"/>
              </section>

              <section>
                <div class="description">
                  <div class="image full-width over-hidden desktop-only" style="aspect-ratio: 1 / .64;">
                    <Image :src="image1"
                           :alt="product.name"/>
                  </div>

                  <ProductSpoiler :catalogs="product.catalogs"
                                  :pdfs="product.pdfs"
                                  :collection_slug="product.collection.slug"
                                  :main_category="product.mainCategory"
                                  :is_tile="product.is_tile"
                                  :niche_id="product.niche_id"
                                  :attributes="product.attribute_groups" :sku="product.sku"
                                  :tab_sections="product.new_texts?.tabs || []"
                                  :attribute_icons="product.attribute_icons"/>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>

    <ProductSelectSide v-if="product.selects && activeSelect!==null" :select_image="product.selects.select_image"
                       @close="activeSelect = null"
                       @loaded="product.selects=$event"
                       :sku="product.sku" :activeSelect="product.selects.selects[activeSelect]"
                       :collection="product.collection"/>

    <div class="row full-width flex-start admin-panel" v-if="userStore.isAdmin && userStore.showAdmin && product.admin">
      <div class="g-16">
        <div>
          <Text>In Stock</Text>
          <Text tag="strong">
            <template v-if="product.admin.is_m2">{{ product.admin.in_stock_boxes }} box</template>
            <template v-else>{{ product.admin.in_stock }} <span v-html="product.admin.unit_block"></span></template>
          </Text>
          <Text v-if="product.admin.is_m2" tag="small">{{ product.admin.in_stock }} <span v-html="product.admin.unit_block"></span></Text>
        </div>
        <div>
          <Text>Supplier Stock</Text>
          <Text tag="strong">
            <template v-if="product.admin.is_m2">{{ product.admin.supplier_stock_boxes }} box</template>
            <template v-else>{{ product.admin.supplier_stock }} <span v-html="product.admin.unit_block"></span></template>
          </Text>
          <Text v-if="product.admin.is_m2" tag="small">{{ product.admin.supplier_stock }} <span v-html="product.admin.unit_block"></span></Text>
        </div>
        <div>
          <Text>Samples in stock</Text>
          <Text tag="strong">{{ product.admin.samples_in_stock }} <span>st</span></Text>
        </div>
        <div>
          <Text>Sold All Time</Text>
          <Text tag="strong">{{ product.admin.sold_all_time }} <span v-html="product.admin.unit_block"></span></Text>
        </div>
      </div>

      <div class="g-16">
        <div>
          <Text>Ready to Sell</Text>
          <Text tag="strong">
            <template v-if="product.admin.is_m2">{{ product.admin.ready_to_sell_boxes }} box</template>
            <template v-else>{{ product.admin.ready_to_sell }} <span v-html="product.admin.unit_block"></span></template>
          </Text>
          <Text v-if="product.admin.is_m2" tag="small">{{ product.admin.ready_to_sell }} <span v-html="product.admin.unit_block"></span></Text>
        </div>
        <div>
          <Text>Incoming</Text>
          <Text tag="strong">
            <template v-if="product.admin.is_m2">{{ product.admin.incoming_boxes }} box</template>
            <template v-else>{{ product.admin.incoming }} <span v-html="product.admin.unit_block"></span></template>
          </Text>
          <Text v-if="product.admin.is_m2" tag="small">{{ product.admin.incoming }} <span v-html="product.admin.unit_block"></span></Text>
        </div>
        <div>
          <Text>Samples Available</Text>
          <Text tag="strong">{{ product.admin?.samples_available }} <span>st</span></Text>
        </div>
        <div>
          <Text>Return Rate</Text>
          <Text tag="strong">
            <template v-if="product.admin?.return_product_qty_total > 0">
              {{ product.admin?.percents_return }}% ({{
                product.admin?.return_product_qty_total
              }}/{{ product.admin?.product_orders_qty }})
            </template>
            <template v-else>None</template>
          </Text>
        </div>
      </div>
    </div>

    <Modal minHeight="95%" v-if="currentBundle" @close="currentBundle=null">
      <template #title>{{ currentBundle.name }}</template>
      <template #default>
        <Supplementary @close="bundleToken=Math.random()" :key="currentBundle.id" :bundle="currentBundle">
          <Button @click="bundleToken=Math.random()" type="bordered" class="tall full-width">Nej, tack!</Button>
        </Supplementary>
      </template>
    </Modal>

    <Modal @close="showSampleForm=false" v-if="showSampleForm" minHeight="95%" style="min-height: 95%;">
      <template #title>Du lägger till följande i kundvagnen:</template>
      <template #default>
        <SampleForm @close="showSampleForm=false" :price="product.prices.standard" :name="product.name"
                    :image="resolveFileImage(product.files.frontal_images[0])"
                    :unit="product.unit"
                    :sku="product.sku" :id="product.id" :delivery-time="product.sample_delivery_time"
                    :sample-price="product.sample_price"
        />
      </template>
    </Modal>

    <div style="margin-top: 50px;">
      <div class="g-96">
        <GreyBlock style="padding-top: 60px;">
          <div class="container full-width g-96" style="min-height: 600px;">
            <section class="mobile-padding" v-if="product.instagram?.length">
              <Instagram :instagram="product.instagram"/>
            </section>
            <section v-if="product.shop_the_looks" v-for="shop_the_look in product.shop_the_looks">
              <ShopTheLook :products="shop_the_look.products" :preview="shop_the_look.file_cut"
                           :image="shop_the_look.file" :id="shop_the_look.id ?? null"/>
            </section>
            <section class="mobile-padding">
              <Reviews :data="reviews"/>
            </section>
            <section v-if="bottomText">
              <ProductMainDescription :image1="image1"
                                      :image2="image2"
                                      :text="bottomText" :sku="product.sku"/>
            </section>
            <section v-else-if="product.description">
              <ProductMainDescription :image1="image1"
                                      :image2="image2"
                                      :text="product.description || ''" :sku="product.sku"/>
            </section>
            <section class="mobile-padding" v-if="product.related_collections?.length">
              <BasicBlock>
                <template v-slot:title>{{ $t("product.related_collections") }}</template>
                <nuxt-link class="all-collections desktop-only" :to="$t('collection.collections_url')">
                  {{ $t('collection.see_all') }}
                  <ExpandIcon/>
                </nuxt-link>
                <CollectionsPreview :collections="product.related_collections"/>
              </BasicBlock>
              <nuxt-link class="all-collections mobile-only" :to="$t('collection.collections_url')">
                {{ $t('collection.see_all') }}
                <ExpandIcon/>
              </nuxt-link>
            </section>
            <section class="mobile-padding" v-if="product.recommended_products.length">
              <BasicBlock>
                <template v-slot:title>{{ $t("product.recommended_products") }}</template>
                <ProductsCarousel :products="product.recommended_products"/>
              </BasicBlock>
            </section>
          </div>
        </GreyBlock>

        <ArticlesCarousel
            v-if="blog_articles.length"
            :articles="blog_articles"
            :title="$t('blog.related_articles')"
        />

        <SalesArguments/>
      </div>
    </div>


    <Shortcode
        v-if="userStore.isAdmin"
        :shortcodes="{
        url: product?.admin?.shortcode_url ?? '',
        name: product?.admin?.shortcode_name ?? ''
    }"
        :bottom="150"
    />


    <AdminButton v-if="userStore.isAdmin">
      <slot name="admin"/>
    </AdminButton>
  </template>
</template>

<script>
export default {
  name: 'ShopSlugPage',
};
</script>

<style scoped lang="scss">
.admin-panel {
  position: fixed;
  z-index: 999;
  bottom: 18%;
  width: 296px;
  right: 20px;
  background: #F9F9F9;
  padding: 20px;
  border-radius: 6px;
}

.container {
  @include mobile {
    padding: 0;
  }
}

.product-breadcrumbs {
  @include smd {
    padding-left: 16px;
    overflow: auto;
  }

  &:deep .breadcrumbs {
    @include smd {
      flex-wrap: nowrap;
    }
  }
}

.sku {
  margin-bottom: 16px;
  color: #707070;
  font-size: 12px;

  @include smd {
    display: none;
  }
}

.all-collections {
  position: absolute;
  right: 10px;
  top: 10px;
  font-weight: 600;
  font-size: 18px;
  display: flex;
  flex-direction: row;
  align-items: center;

  @include mobile {
    position: relative;
    margin-left: auto;
    width: 100%;
    text-align: right;
    justify-content: end;
    margin-top: 20px;
    font-weight: 500;
  }
}

.main-info {
  display: block;
}

.gallery {
  margin-bottom: 24px;

  @include mobile {
    margin-bottom: 16px;
  }

  &:deep(.numbers) {
    @include smd {
      font-size: 30px;
    }
  }

  &:deep(.meaning) {
    @include smd {
      font-size: 10px !important;
      margin-top: 5px;
      font-weight: 500 !important;
    }
  }

  &:deep(.dots) {
    @include smd {
      margin: 0px 5px 0;
    }
  }
}

.main-info-description {
  padding: 16px 12px;
  background: $violet;

  &.bg-blue {
    background: rgba($blue, .1);
  }

  &.bg-green {
    background: rgba($green, .1);
  }
}

.description-info {
  font-size: 13px;

  h2 {
    font-size: inherit;
    margin: 0;
    font-weight: 600;
  }
}

.description-info:deep(a) {
  color: $blue;
}

// .description-info:deep(ul) {
//   padding: 0;
// }

// .description-info:deep(ol) {
//   padding: 0;
// }

.description-info:deep(div) {
  display: block;
}

.description-info:deep(iframe) {
  width: 100%;
}

.right-column {
  display: block;
  margin-right: 37px;
  width: 32%;
  float: right;
  position: sticky;
  position: -webkit-sticky;
  top: 10px; // overridden on desktop by JS when column is taller than viewport
  align-self: flex-start;

  @include small-desktop {
    width: 31%;
  }

  @include medium {
    width: 40%;
    margin-right: 0;
  }

  @include smd {
    position: relative;
    width: 100%;
    top: 0;
    margin-bottom: 24px;
    margin-right: 0;
  }
}

.left-column {
  padding-right: 65px;
  justify-content: start;
  gap: 20px;
  width: 65%;
  display: inline-block;
  //z-index: -1;

  &.breadcrumbs {
    @include smd {
      margin-bottom: 8px;
    }
  }

  @include medium {
    padding-right: 0;
    width: 56%;
  }

  @include smd {
    padding-right: unset;
    max-width: unset;
    width: 100%;
  }
}

.mobile-only {
  display: none;

  @include smd {
    display: block;
  }
}

.desktop-only {
  display: block;

  @include smd {
    display: none;
  }
}

.admin-panel {
  strong {
    font-size: 18px;
  }
}
</style>
